/**
 * VITRE : pas de disque du tout. Une fenetre de verre depoli, une lumiere chaude derriere, et
 * quelqu'un — un buste flou, sans visage ni prenom.
 *
 * L'IDEE, EN UN MOT : une presence humaine sans promesse de personne. Le poids passe d'une jambe
 * a l'autre, la tete se tourne, la lumiere respire ; rien de tout cela ne represente un agent
 * nomme, donc rien ne se dement quand c'est un assistant qui repond.
 *
 * LE FLOU EST LE SUJET, PAS UN EFFET : c'est lui qui empeche la silhouette de devenir un portrait.
 * Il ne se leve qu'a l'approche — au temps fort et au survol — et jamais completement.
 *
 * VITRE EST PLUS HAUT QUE LARGE (64 sur 84) et deborde donc VERS LE HAUT du rond de la bulle : la
 * fenetre monte au-dessus, la zone cliquable reste le rond. C'est le second look sans disque, avec
 * Aurore, et la meme raison : rien de tout cela ne tiendrait dans le bouton, qui rogne.
 *
 * AU TEMPS FORT elle s'approche et pose la main sur la vitre. A L'OUVERTURE elle s'eloigne dans le
 * flou, la lumiere baisse, et une croix se trace dans la buee.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT, et sa feuille est un gabarit de chaine : deux accents
 * graves dans le fichier, pas un de plus.
 */
export default {
  id: 'vitre',

  css: `
/* LA FENETRE MONTE PLUS HAUT QUE LA BULLE. Le lanceur est ancre en bas : l'agrandir en hauteur le
   fait deborder vers le haut, la ou il n'y a rien, et jamais vers le bas ou vit la barre du site. */
.woot-lanceur[data-look="vitre"] { height: 84px; }

/* Le cartouche redescend a hauteur du rond cliquable : centre sur 84 px, il flotterait au milieu
   de la vitre au lieu d'accompagner la bulle. */
.woot-lanceur[data-look="vitre"] .woot-lanceur__cartouche { top: 58%; }

/* LA LUMIERE EST DERRIERE LA VITRE : une tache chaude, tres floue, qui respire. La couleur du
   client tiree vers l'ambre — une lumiere allumee dans une piece, pas un ecran. */
.woot-lanceur[data-look="vitre"] .woot-vitre__lumiere {
  position: absolute;
  left: 8px;
  right: 8px;
  top: 18px;
  bottom: -2px;
  border-radius: 50%;
  background: var(--client);
  background: color-mix(in srgb, var(--client) 72%, #ffb27a);
  filter: blur(12px);
  opacity: .5;
  transition: opacity .5s ease;
}

.woot-lanceur[data-look="vitre"][data-etat="repos"] .woot-vitre__lumiere,
.woot-lanceur[data-look="vitre"][data-etat="attention"] .woot-vitre__lumiere {
  animation: woot-vitre-lumiere 7s ease-in-out infinite;
}

@keyframes woot-vitre-lumiere {
  0%, 100% { opacity: .42; transform: translateY(0); }
  50% { opacity: .66; transform: translateY(-3px); }
}

/* LE VERRE : un panneau translucide, jamais du blanc pose sur une page inconnue. Ce qui le rend
   lisible sur page claire comme sur page sombre, c'est son filet a la couleur du client et la
   lumiere qui passe derriere, pas un fond opaque. */
.woot-lanceur[data-look="vitre"] .woot-vitre__verre {
  position: absolute;
  left: 9px;
  top: 4px;
  width: 46px;
  height: 78px;
  border-radius: 23px 23px 9px 9px;
  background: linear-gradient(165deg, rgba(255, 255, 255, .34) 0%, rgba(255, 255, 255, .12) 45%, rgba(255, 255, 255, .2) 100%);
  backdrop-filter: blur(6px) saturate(1.1);
  border: 1px solid rgba(255, 255, 255, .6);
  border: 1px solid color-mix(in srgb, var(--client) 32%, white);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .7), inset 0 -1px 0 rgba(0, 0, 0, .08), 0 10px 26px rgba(16, 19, 23, .18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .7), inset 0 -1px 0 rgba(0, 0, 0, .08), 0 10px 26px color-mix(in srgb, var(--client) 22%, transparent);
  overflow: hidden;
}

/* Le reflet oblique fixe de la vitre : c'est lui qui dit « du verre » et non « du plastique ». */
.woot-lanceur[data-look="vitre"] .woot-vitre__verre::before {
  content: "";
  position: absolute;
  inset: -20%;
  background: linear-gradient(118deg, transparent 38%, rgba(255, 255, 255, .26) 47%, rgba(255, 255, 255, .05) 52%, transparent 60%);
}

.woot-lanceur[data-look="vitre"] .woot-vitre__verre svg {
  position: absolute;
  left: 0;
  top: 0;
  width: 46px;
  height: 78px;
  overflow: visible;
}

/* LE BUSTE. Le flou et l'echelle passent par les proprietes individuelles scale et translate, pas
   par transform : la respiration occupe deja transform, et les deux se marcheraient dessus. */
.woot-lanceur[data-look="vitre"] .woot-vitre__buste {
  fill: #ffffff;
  opacity: .95;
  filter: blur(2.8px);
  transform-box: fill-box;
  transform-origin: 50% 100%;
  transition: scale .6s cubic-bezier(.2, .8, .2, 1), translate .6s cubic-bezier(.2, .8, .2, 1), filter .5s ease, opacity .4s ease;
}

.woot-lanceur[data-look="vitre"][data-etat="repos"] .woot-vitre__buste,
.woot-lanceur[data-look="vitre"][data-etat="attention"] .woot-vitre__buste {
  animation: woot-vitre-poids 7s ease-in-out infinite;
}

@keyframes woot-vitre-poids {
  0%, 100% { transform: translateX(-1px) rotate(-.6deg); }
  50% { transform: translateX(1.2px) rotate(.6deg); }
}

.woot-lanceur[data-look="vitre"] .woot-vitre__tete {
  transform-box: fill-box;
  transform-origin: 50% 90%;
}

.woot-lanceur[data-look="vitre"][data-etat="repos"] .woot-vitre__tete,
.woot-lanceur[data-look="vitre"][data-etat="attention"] .woot-vitre__tete {
  animation: woot-vitre-tourne 5.5s ease-in-out infinite;
}

@keyframes woot-vitre-tourne {
  0%, 100% { transform: rotate(-3deg) scaleX(1); }
  50% { transform: rotate(2deg) scaleX(.96); }
}

/* LE TEMPS FORT : elle s'approche, le flou se leve un peu, et une main se pose sur la vitre. La
   main n'existe qu'a ce moment — au repos elle est transparente et immobile. */
.woot-lanceur[data-look="vitre"] .woot-vitre__main {
  fill: #ffffff;
  opacity: 0;
  filter: blur(2.4px);
  transform-box: fill-box;
  transform-origin: 50% 100%;
}

.woot-lanceur[data-look="vitre"][data-etat="attention"] .woot-vitre__buste {
  animation: woot-vitre-poids 7s ease-in-out infinite, woot-vitre-approche 2.2s ease-in-out;
}

@keyframes woot-vitre-approche {
  0%, 100% { scale: 1; filter: blur(2.8px); }
  35%, 70% { scale: 1.1; filter: blur(1.4px); }
}

.woot-lanceur[data-look="vitre"][data-etat="attention"] .woot-vitre__main {
  animation: woot-vitre-touche 2.2s ease-in-out;
}

@keyframes woot-vitre-touche {
  0% { opacity: 0; transform: translateY(14px); filter: blur(2.4px); }
  30% { opacity: .95; transform: translateY(0); filter: blur(.8px); }
  65% { opacity: .95; transform: translateY(0); filter: blur(.8px); }
  100% { opacity: 0; transform: translateY(10px); filter: blur(2.4px); }
}

/* AU SURVOL elle se rapproche et la piece s'eclaire : c'est la meme approche que le temps fort,
   tenue tant que le pointeur reste. */
.woot-lanceur[data-look="vitre"]:hover .woot-vitre__buste { filter: blur(1.4px); scale: 1.06; }
.woot-lanceur[data-look="vitre"]:hover .woot-vitre__lumiere { opacity: .85; }

/* LA PASTILLE DE PRESENCE : le seul element opaque du look, et le seul qui ne soit pas a la
   couleur du client — un vert de disponibilite se lit comme tel partout. Son cercle est cerne par
   la surface du theme pour se detacher d'une page claire comme d'une page sombre. */
.woot-lanceur[data-look="vitre"] .woot-vitre__presence {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid var(--woot-surface);
}

.woot-lanceur[data-look="vitre"] .woot-vitre__presence::after {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  border: 1.5px solid #22c55e;
}

.woot-lanceur[data-look="vitre"][data-etat="repos"] .woot-vitre__presence::after,
.woot-lanceur[data-look="vitre"][data-etat="attention"] .woot-vitre__presence::after {
  animation: woot-vitre-onde 2.4s ease-out infinite;
}

@keyframes woot-vitre-onde {
  0% { transform: scale(.7); opacity: .9; }
  100% { transform: scale(2.2); opacity: 0; }
}

/* OUVERT : elle s'eloigne dans le flou, la lumiere baisse, et une croix se trace dans la buee.
   La croix se pose au milieu de la VITRE, pas du lanceur : le centre du lanceur est ailleurs
   puisqu'il est deux fois plus haut que large. Sa couleur passe par le fond des deux barres et
   non par --woot-croix : LE VERRE EST TRANSLUCIDE, donc la croix se lit sur la page derriere lui
   et non sur la couleur. Elle prend la couleur de l'inbox tant que celle-ci s'y lit, et l'encre du
   theme quand elle est trop pale. Le second argument du var() est la chaine de repli. */
.woot-lanceur[data-look="vitre"] .woot-lanceur__croix {
  left: 32px;
  top: 43px;
  width: 20px;
  height: 20px;
  margin: -10px;
  filter: blur(.4px);
}

.woot-lanceur[data-look="vitre"] .woot-lanceur__croix::before,
.woot-lanceur[data-look="vitre"] .woot-lanceur__croix::after {
  background: var(--woot-encre-auto, var(--client));
  box-shadow: 0 0 6px rgba(255, 255, 255, .5);
}

.woot-lanceur[data-look="vitre"][data-etat="ouvert"] .woot-vitre__buste {
  opacity: 0;
  scale: .88;
  translate: 0 6px;
  filter: blur(4px);
}

.woot-lanceur[data-look="vitre"][data-etat="ouvert"] .woot-vitre__lumiere { opacity: .28; }
`,

  monter: conteneur => {
    const lumiere = document.createElement('span');
    lumiere.className = 'woot-vitre__lumiere';

    const verre = document.createElement('div');
    verre.className = 'woot-vitre__verre';
    verre.innerHTML =
      '<svg viewBox="0 0 46 78" aria-hidden="true">' +
      '<g class="woot-vitre__buste">' +
      '<path d="M1 78 C 1 60, 8 48, 18 42 L 28 42 C 38 48, 45 60, 45 78 Z"/>' +
      '<path d="M19 33 L 27 33 L 28.5 43 L 17.5 43 Z"/>' +
      '<g class="woot-vitre__tete">' +
      '<ellipse cx="23" cy="25" rx="9.6" ry="10.8" transform="rotate(-4 23 25)"/>' +
      '</g></g>' +
      '<rect class="woot-vitre__main" x="31" y="38" width="10" height="14" rx="4.5"/>' +
      '</svg>';

    const croix = document.createElement('span');
    croix.className = 'woot-lanceur__croix';

    const presence = document.createElement('span');
    presence.className = 'woot-vitre__presence';

    conteneur.appendChild(lumiere);
    conteneur.appendChild(verre);
    conteneur.appendChild(croix);
    conteneur.appendChild(presence);
  },

  // VITRE EST DU CSS PUR : les quatre gestes existent parce que le modele les appelle pour tous
  // les looks, et parce que certains — une bouche qui parle en SMIL — en ont besoin. Ici l'attribut
  // d'etat porte tout, et il n'y a rien a mettre en pause que la feuille n'arrete deja.
  gestes: {
    attention: () => {},
    ouvrir: () => {},
    fermer: () => {},
    reduireLeMouvement: () => {},
  },
};
